import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Package, Calendar, MapPin, Phone, Mail, MessageSquare, 
  ArrowLeft, Download, Share2, Copy, CheckCircle, Clock,
  DollarSign, Truck, Star, Eye, FileText, XCircle, Edit3,
  Trash2, Plus, AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';

interface Pedido {
  id: number;
  tipo_equipamento: string;
  marca: string;
  modelo: string;
  tipo_peca: string;
  descricao: string;
  status: string;
  data_criacao: string;
  cep: string;
  imagem?: string;
  orcamentos_count: number;
  fornecedor_escolhido?: string;
  valor_final?: number;
  fornecedor_escolhido_detalhes?: {
    nome: string;
    telefone: string;
    email: string;
    whatsapp: string;
  };
  orcamento_escolhido?: {
    id: number;
    valor: number;
    condicao_peca: string;
    garantia: string;
    tempo_envio: string;
    tipo_entrega: string;
    frete_incluso: boolean;
    observacoes: string;
  };
  orcamentos?: Array<{
    id: number;
    fornecedor: string;
    valor: number;
    condicao_peca: string;
    garantia: string;
    tempo_envio: string;
    tipo_entrega: string;
    frete_incluso: boolean;
    observacoes: string;
    escolhido: boolean;
    data_recebimento?: string;
  }>;
  status_timeline?: {
    criado?: string;
    em_andamento?: string;
    orcamentos_recebidos?: string;
    aprovado?: string;
    finalizado?: string;
    reprovado?: string;
  };
  orcamentos_timeline?: Array<{
    id: number;
    fornecedor: string;
    data_recebimento: string;
    valor: number;
  }>;
  valor_estimado?: number;
  urgencia?: string;
  prazo_desejado?: string;
  observacoes?: string;
  cliente_nome?: string;
  empresa?: string;
  total_orcamentos?: number;
  orcamento_maximo?: number;
  categoria?: string;
  marca_texto?: string;
  modelo_texto?: string;
}

const PedidoDetalhes: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllOrcamentos, setShowAllOrcamentos] = useState(false);
  
  // Estados para ações do pedido
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddInfoModal, setShowAddInfoModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isActionLoading, setIsActionLoading] = useState(false);
  
  // Estados para adicionar informações
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);

  useEffect(() => {
    loadPedido();
  }, [id]);

  const generateWhatsAppMessage = (pedido: Pedido) => {
    const pecaInfo = `${pedido.tipo_peca} para ${pedido.tipo_equipamento} ${pedido.marca} ${pedido.modelo}`;
    const valorFormatado = pedido.valor_final?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    
    return `Olá! Vim do site PeçaJá para falar do orçamento #${pedido.orcamento_escolhido?.id} referente a ${pecaInfo} no valor de ${valorFormatado}. Poderia me ajudar com mais informações sobre este orçamento?`;
  };

  const maskCompanyName = (companyName: string) => {
    if (!companyName) return '';
    const words = companyName.split(' ');
    return words.map(word => word.charAt(0)).join('.') + '...';
  };

  const loadPedido = async () => {
    try {
      setIsLoading(true);
      console.log('Carregando pedido ID:', id);
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }

      try {
        const response = await fetch(`/api/pedidos/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const responseData = await response.json();
          console.log('Resposta da API:', responseData);
          
          const pedidoData = responseData.pedido;
          console.log('Pedido carregado da API:', pedidoData);
          
          // Mapear orçamentos da API para timeline
          const orcamentosApi = responseData.pedido?.orcamentos || [];
          const orcamentos_timeline = orcamentosApi.map((orc: any) => ({
            id: orc.id,
            fornecedor: orc.nome_empresa || orc.fornecedor_nome || 'Fornecedor',
            data_recebimento: orc.created_at || new Date().toISOString(),
            valor: orc.valor || orc.valor_final || orc.valor_total || 0
          }));
          
          // Criar timeline baseada no status e orçamentos reais
          const timeline: any = { criado: pedidoData.created_at || new Date().toISOString() };
          
          if (pedidoData.status !== 'pendente') {
            timeline.em_andamento = new Date(Date.parse(pedidoData.created_at) + 24 * 60 * 60 * 1000).toISOString();
          }
          
          if (orcamentos_timeline.length > 0) {
            timeline.orcamentos_recebidos = orcamentos_timeline[0].data_recebimento;
          }
          
          if (pedidoData.status === 'aprovado' || pedidoData.status === 'finalizado') {
            timeline.aprovado = new Date(Date.parse(pedidoData.created_at) + 48 * 60 * 60 * 1000).toISOString();
          }
          
          if (pedidoData.status === 'finalizado') {
            timeline.finalizado = new Date(Date.parse(pedidoData.created_at) + 72 * 60 * 60 * 1000).toISOString();
          }
          
          // Mapear dados da API para o formato esperado
          const pedidoEncontrado: Pedido = {
            id: pedidoData.id,
            tipo_equipamento: pedidoData.tipo_equipamento || pedidoData.categoria || 'Equipamento',
            marca: pedidoData.marca || pedidoData.marca_texto || 'Marca',
            modelo: pedidoData.modelo || pedidoData.modelo_texto || 'Modelo',
            tipo_peca: pedidoData.tipo_peca || 'Peça',
            descricao: pedidoData.descricao || pedidoData.titulo || 'Descrição do pedido',
            status: pedidoData.status || 'pendente',
            data_criacao: pedidoData.created_at || new Date().toISOString(),
            cep: pedidoData.cep || '00000-000',
            orcamentos_count: orcamentos_timeline.length,
            valor_estimado: pedidoData.orcamento_maximo || 0,
            urgencia: pedidoData.urgencia || 'normal',
            prazo_desejado: pedidoData.prazo_desejado || null,
            observacoes: pedidoData.observacoes || '',
            cliente_nome: pedidoData.cliente_nome || 'Cliente',
            empresa: pedidoData.empresa || pedidoData.cliente_empresa || 'Empresa',
            status_timeline: timeline,
            orcamentos_timeline: orcamentos_timeline
          };
          
          setPedido(pedidoEncontrado);
          return;
        } else if (response.status === 404) {
          console.log('Pedido não encontrado na API, usando dados mockados');
        }
      } catch (apiError) {
        console.log('Erro ao buscar da API, usando dados mockados:', apiError);
      }

      // Fallback para dados mockados se a API não estiver disponível
      await new Promise(resolve => setTimeout(resolve, 1000));
      const pedidoId = parseInt(id || '1');
      
      // Usar o mesmo mapeamento da página de detalhes
      const orcamentosMapping: { [key: number]: number } = {
        4: 1,  // ID 4 tem 1 orçamento
        7: 2,  // ID 7 tem 2 orçamentos
        10: 1, // ID 10 tem 1 orçamento
        13: 2, // ID 13 tem 2 orçamentos
        16: 1, // ID 16 tem 1 orçamento
        19: 2, // ID 19 tem 2 orçamentos
        22: 1, // ID 22 tem 1 orçamento
        25: 2, // ID 25 tem 2 orçamentos
        28: 1, // ID 28 tem 1 orçamento
        31: 2  // ID 31 tem 2 orçamentos
      };
      
      const orcamentosCount = orcamentosMapping[pedidoId] !== undefined ? orcamentosMapping[pedidoId] : pedidoId % 3;
      
      // Gerar dados dinamicamente baseados no ID
      const dataCriacao = new Date(Date.now() - (pedidoId * 24 * 60 * 60 * 1000));
      const statusOptions = ['pendente', 'em_andamento', 'aprovado', 'finalizado', 'cancelado', 'reprovado'];
      const status = statusOptions[pedidoId % statusOptions.length];
      
      let statusFinal = status;
      if (orcamentosCount > 0) {
        statusFinal = 'em_andamento';
      } else if (orcamentosCount === 0 && (status === 'em_andamento' || status === 'aprovado' || status === 'finalizado')) {
        statusFinal = 'aguardando_orcamentos';
      }
      
      // Criar timeline baseada no status
      const timeline: any = { criado: dataCriacao.toISOString() };
      
      if (statusFinal === 'em_andamento' || statusFinal === 'aprovado' || statusFinal === 'finalizado') {
        timeline.em_andamento = new Date(dataCriacao.getTime() + 24 * 60 * 60 * 1000).toISOString();
      }
      
      if (orcamentosCount > 0) {
        timeline.orcamentos_recebidos = new Date(dataCriacao.getTime() + 24 * 60 * 60 * 1000).toISOString();
      }
      
      if (statusFinal === 'aprovado' || statusFinal === 'finalizado') {
        timeline.aprovado = new Date(dataCriacao.getTime() + 48 * 60 * 60 * 1000).toISOString();
      }
      
      if (statusFinal === 'finalizado') {
        timeline.finalizado = new Date(dataCriacao.getTime() + 72 * 60 * 60 * 1000).toISOString();
      }
      
      // Criar orçamentos timeline se houver orçamentos
      const orcamentos_timeline = orcamentosCount > 0 ? Array.from({ length: orcamentosCount }, (_, i) => ({
        id: pedidoId * 10 + i + 1,
        fornecedor: `Fornecedor ${i + 1}`,
        data_recebimento: new Date(dataCriacao.getTime() + (24 + i * 2) * 60 * 60 * 1000).toISOString(),
        valor: 12000 + (pedidoId * 100) + (i * 500)
      })) : [];
      
              const pedidoEncontrado: Pedido = {
          id: pedidoId,
          tipo_equipamento: pedidoId % 3 === 0 ? 'Trator' : pedidoId % 2 === 0 ? 'Pá Carregadeira' : 'Escavadeira',
          marca: pedidoId % 3 === 0 ? 'John Deere' : pedidoId % 2 === 0 ? 'Komatsu' : 'Caterpillar',
          modelo: pedidoId % 3 === 0 ? '750J' : pedidoId % 2 === 0 ? 'WA380' : '320D',
          tipo_peca: pedidoId % 3 === 0 ? 'Transmissão' : pedidoId % 2 === 0 ? 'Sistema Hidráulico' : 'Motor',
          descricao: `${pedidoId % 3 === 0 ? 'Transmissão' : pedidoId % 2 === 0 ? 'Sistema hidráulico' : 'Motor'} completo para ${pedidoId % 3 === 0 ? 'trator' : pedidoId % 2 === 0 ? 'pá carregadeira' : 'escavadeira'} ${pedidoId % 3 === 0 ? 'John Deere 750J' : pedidoId % 2 === 0 ? 'Komatsu WA380' : 'Caterpillar 320D'}`,
          status: statusFinal,
          data_criacao: dataCriacao.toISOString(),
          cep: `${String(pedidoId).padStart(5, '0')}-${String(pedidoId * 2).padStart(3, '0')}`,
          orcamentos_count: orcamentosCount,
          valor_estimado: 15000 + (pedidoId * 500),
          urgencia: pedidoId % 3 === 0 ? 'alta' : pedidoId % 2 === 0 ? 'normal' : 'baixa',
          prazo_desejado: new Date(Date.now() + (pedidoId * 7 * 24 * 60 * 60 * 1000)).toISOString(),
          observacoes: `Observações específicas para o pedido ${pedidoId}. ${pedidoId % 2 === 0 ? 'Urgente' : 'Normal'}.`,
          cliente_nome: 'João Silva',
          empresa: 'Construtora Silva Ltda',
          status_timeline: timeline,
          orcamentos_timeline: orcamentos_timeline
        };
      
      setPedido(pedidoEncontrado);
      console.log('Pedido carregado (mockado):', pedidoEncontrado);
    } catch (error) {
      console.error('Erro ao carregar pedido:', error);
      toast.error('Erro ao carregar pedido');
      navigate('/cliente/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  // Função para cancelar pedido
  const handleCancelPedido = async () => {
    if (!pedido) return;
    
    setIsActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/pedidos/${pedido.id}/cancelar`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ motivo: cancelReason })
      });

      if (response.ok) {
        toast.success('Pedido cancelado com sucesso!');
        setShowCancelModal(false);
        setCancelReason('');
        loadPedido(); // Recarregar dados
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao cancelar pedido');
      }
    } catch (error) {
      console.error('Erro ao cancelar pedido:', error);
      toast.error('Erro ao cancelar pedido');
    } finally {
      setIsActionLoading(false);
    }
  };

  // Função para verificar se pode cancelar
  const canCancelPedido = () => {
    if (!pedido) return false;
    return pedido.status !== 'finalizado' && pedido.status !== 'cancelado';
  };

  // Função para verificar se pode editar
  const canEditPedido = () => {
    if (!pedido) return false;
    return ['pendente', 'em_analise', 'recebendo_orcamentos'].includes(pedido.status);
  };

  // Função para verificar se pode adicionar informações
  const canAddInfo = () => {
    if (!pedido) return false;
    return pedido.status !== 'cancelado' && pedido.status !== 'concluido';
  };

  // Função para adicionar informações complementares
  const handleAddInfo = async () => {
    if (!pedido) return;
    
    if (!additionalInfo.trim() && !selectedFiles) {
      toast.error('Digite pelo menos uma observação ou selecione um arquivo');
      return;
    }
    
    console.log('🔧 Iniciando adicionar informações:', {
      pedidoId: pedido.id,
      hasAdditionalInfo: !!additionalInfo.trim(),
      filesCount: selectedFiles ? selectedFiles.length : 0
    });
    
    setIsActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      console.log('🔑 Token disponível:', !!token);
      
      const formData = new FormData();
      
      if (additionalInfo.trim()) {
        formData.append('observacoes_adicionais', additionalInfo.trim());
      }
      
      if (selectedFiles) {
        for (let i = 0; i < selectedFiles.length; i++) {
          formData.append('arquivos', selectedFiles[i]);
          console.log('📎 Arquivo adicionado:', selectedFiles[i].name);
        }
      }

      console.log('🌐 Fazendo requisição para:', `/api/pedidos/${pedido.id}/adicionar-info`);
      
      const response = await fetch(`/api/pedidos/${pedido.id}/adicionar-info`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });
      
      console.log('📊 Resposta recebida:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Informações adicionadas com sucesso!');
        setShowAddInfoModal(false);
        setAdditionalInfo('');
        setSelectedFiles(null);
        loadPedido(); // Recarregar dados
        
        if (result.fornecedores_notificados > 0) {
          toast(`📢 ${result.fornecedores_notificados} fornecedor(es) foram notificados sobre as novas informações`, {
            icon: '📢',
            duration: 4000,
          });
        }
      } else {
        let errorMessage = 'Erro ao adicionar informações';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (jsonError) {
          // Se não conseguir fazer parse do JSON, usar a resposta como texto
          const errorText = await response.text();
          errorMessage = errorText || `Erro HTTP ${response.status}`;
        }
        console.error('Erro detalhado:', {
          status: response.status,
          statusText: response.statusText,
          message: errorMessage
        });
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Erro ao adicionar informações:', error);
      toast.error('Erro ao adicionar informações');
    } finally {
      setIsActionLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aguardando_orcamentos':
        return 'text-blue-600 bg-blue-100';
      case 'em_andamento':
        return 'text-blue-600 bg-blue-100';
      case 'aprovado':
        return 'text-purple-600 bg-purple-100';
      case 'reprovado':
        return 'text-red-600 bg-red-100';
      case 'finalizado':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'aguardando_orcamentos':
        return <Package className="w-4 h-4" />;
      case 'em_andamento':
        return <Clock className="w-4 h-4" />;
      case 'aprovado':
        return <CheckCircle className="w-4 h-4" />;
      case 'reprovado':
        return <XCircle className="w-4 h-4" />;
      case 'finalizado':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'aguardando_orcamentos':
        return 'Aguardando Orçamentos';
      case 'em_andamento':
        return 'Em Andamento';
      case 'aprovado':
        return 'Aprovado';
      case 'reprovado':
        return 'Reprovado';
      case 'finalizado':
        return 'Finalizado';
      default:
        return status;
    }
  };

  const handleDuplicarPedido = () => {
    // Criar um novo pedido com os mesmos dados
    const novoPedido = {
      ...pedido,
      id: Date.now(), // Novo ID
      data_criacao: new Date().toISOString(),
      status: 'aguardando_orcamentos',
      orcamentos_count: 0,
      fornecedor_escolhido: undefined,
      valor_final: undefined,
      status_timeline: {
        criado: new Date().toISOString(),
        em_andamento: undefined,
        orcamentos_recebidos: undefined,
        aprovado: undefined,
        finalizado: undefined,
        reprovado: undefined,
      }
    };
    
    // Aqui você faria a chamada para a API para criar o novo pedido
    toast.success('Pedido duplicado com sucesso!');
    navigate('/cliente/dashboard');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando detalhes do pedido...</p>
        </div>
      </div>
    );
  }

  if (!pedido) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Pedido não encontrado</h2>
          <p className="text-gray-600 mb-4">O pedido solicitado não foi encontrado.</p>
          <button
            onClick={() => navigate('/cliente/dashboard')}
            className="btn btn-primary"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="/logo-pecaja.png" 
                alt="PeçaJá & Sobratema" 
                className="h-12 w-auto object-contain"
              />
            </div>

            <button
              onClick={() => navigate('/cliente/dashboard')}
              className="btn btn-outline"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Dashboard
            </button>
          </div>
        </div>
      </header>

      <div className="container py-8">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button
            onClick={() => navigate('/cliente/dashboard')}
            className="breadcrumb-item"
          >
            Dashboard
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Pedido #{pedido.id}</span>
        </div>

        {/* Pedido Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="card">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {pedido.tipo_equipamento} {pedido.marca} {pedido.modelo}
                  </h1>
                  <p className="text-lg text-gray-600">
                    <span className="font-medium">Peça:</span> {pedido.tipo_peca}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(pedido.status)}`}>
                    <div className="flex items-center space-x-1">
                      {getStatusIcon(pedido.status)}
                      <span>{getStatusText(pedido.status)}</span>
                    </div>
                  </span>
                </div>
              </div>

              {/* Ações do Pedido */}
              <div className="border-t pt-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações do Pedido</h3>
                <div className="flex flex-wrap gap-3">
                  {canEditPedido() && (
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="btn btn-outline flex items-center space-x-2"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span>Editar Pedido</span>
                    </button>
                  )}
                  
                  {canAddInfo() && (
                    <button
                      onClick={() => setShowAddInfoModal(true)}
                      className="btn btn-outline flex items-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Adicionar Informações</span>
                    </button>
                  )}
                  
                  {canCancelPedido() && (
                    <button
                      onClick={() => setShowCancelModal(true)}
                      className="btn btn-outline border-orange-500 text-orange-600 hover:bg-orange-50 flex items-center space-x-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Cancelar Pedido</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                {/* Descrição */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Descrição</h3>
                  <p className="text-gray-600 leading-relaxed">
                    {pedido.descricao}
                  </p>
                </div>

                {/* Informações */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Data de Criação</p>
                      <p className="font-medium text-gray-900">
                        {new Date(pedido.data_criacao).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Localização</p>
                      <p className="font-medium text-gray-900">CEP: {pedido.cep}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Package className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Orçamentos Recebidos</p>
                      <p className="font-medium text-gray-900">{pedido.orcamentos_count}</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">ID do Pedido</p>
                      <p className="font-medium text-gray-900">#{pedido.id}</p>
                    </div>
                  </div>
                </div>

                {/* Fornecedor Escolhido */}
                {pedido.fornecedor_escolhido && pedido.fornecedor_escolhido_detalhes && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Fornecedor Escolhido</h3>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center space-x-3 mb-4">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-green-900">{pedido.fornecedor_escolhido_detalhes.nome}</p>
                          {pedido.valor_final && (
                            <p className="text-sm text-green-700">
                              Valor: R$ {pedido.valor_final.toLocaleString('pt-BR')}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Detalhes do Orçamento Escolhido */}
                      {pedido.orcamento_escolhido && (
                        <div className="bg-gray-50 rounded-lg p-4 border border-green-300">
                          <h4 className="text-sm font-semibold text-green-800 mb-3">Detalhes do Orçamento #{pedido.orcamento_escolhido.id}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-green-700"><span className="font-medium">Condição da Peça:</span> {pedido.orcamento_escolhido.condicao_peca}</p>
                              <p className="text-green-700"><span className="font-medium">Garantia:</span> {pedido.orcamento_escolhido.garantia}</p>
                              <p className="text-green-700"><span className="font-medium">Tempo de Envio:</span> {pedido.orcamento_escolhido.tempo_envio}</p>
                            </div>
                            <div>
                              <p className="text-green-700"><span className="font-medium">Tipo de Entrega:</span> {pedido.orcamento_escolhido.tipo_entrega}</p>
                              <p className="text-green-700"><span className="font-medium">Frete Incluso:</span> {pedido.orcamento_escolhido.frete_incluso ? 'Sim' : 'Não'}</p>
                              <p className="text-green-700"><span className="font-medium">Valor:</span> R$ {pedido.orcamento_escolhido.valor.toLocaleString('pt-BR')}</p>
                            </div>
                          </div>
                          {pedido.orcamento_escolhido.observacoes && (
                            <div className="mt-4 pt-3 border-t border-green-200">
                              <p className="text-sm text-green-700"><span className="font-medium">Observações:</span></p>
                              <p className="text-sm text-green-600 mt-1">{pedido.orcamento_escolhido.observacoes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      <a
                        href={`https://wa.me/55${pedido.fornecedor_escolhido_detalhes.whatsapp}?text=${encodeURIComponent(generateWhatsAppMessage(pedido))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center px-6 py-4 rounded-lg bg-green-500 text-white font-semibold text-lg shadow hover:bg-green-600 transition-colors w-full"
                      >
                        <MessageSquare className="w-6 h-6 mr-2" /> WhatsApp
                      </a>
                      <a
                        href={`tel:${pedido.fornecedor_escolhido_detalhes.telefone.replace(/[^\d]/g, '')}`}
                        className="flex items-center justify-center px-6 py-4 rounded-lg bg-blue-500 text-white font-semibold text-lg shadow hover:bg-blue-600 transition-colors w-full"
                      >
                        <Phone className="w-6 h-6 mr-2" /> Ligar
                      </a>
                      <a
                        href={`mailto:${pedido.fornecedor_escolhido_detalhes.email}`}
                        className="flex items-center justify-center px-6 py-4 rounded-lg bg-red text-white font-semibold text-lg shadow hover:bg-red/80 transition-colors w-full"
                      >
                        <Mail className="w-6 h-6 mr-2" /> E-mail
                      </a>
                    </div>
                  </div>
                )}

                {/* Todos os Orçamentos Recebidos */}
                {pedido.orcamentos && pedido.orcamentos.length > 0 && (
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Todos os Orçamentos Recebidos</h3>
                      <button
                        onClick={() => setShowAllOrcamentos(!showAllOrcamentos)}
                        className="btn btn-outline"
                        style={{ minWidth: 'fit-content', whiteSpace: 'nowrap' }}
                      >
                        {showAllOrcamentos ? (
                          <>
                            <span>Ocultar</span>
                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </>
                        ) : (
                          <>
                            <span>Ver todos ({pedido.orcamentos.length})</span>
                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </>
                        )}
                      </button>
                    </div>
                    
                    {showAllOrcamentos && (
                      <div className="space-y-4">
                        {pedido.orcamentos.map((orcamento) => (
                          <div
                            key={orcamento.id}
                            className={`border rounded-lg p-4 transition-all duration-200 ${
                              orcamento.escolhido
                                ? 'bg-green-50 border-green-300 shadow-sm'
                                : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center space-x-3">
                                {orcamento.escolhido && (
                                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                                )}
                                <div>
                                  <p className={`font-medium ${
                                    orcamento.escolhido ? 'text-green-900' : 'text-gray-900'
                                  }`}>
                                    {orcamento.escolhido ? orcamento.fornecedor : maskCompanyName(orcamento.fornecedor)}
                                  </p>
                                  <p className={`text-lg font-bold ${
                                    orcamento.escolhido ? 'text-green-800' : 'text-gray-800'
                                  }`}>
                                    R$ {orcamento.valor.toLocaleString('pt-BR')}
                                  </p>
                                </div>
                              </div>
                              {orcamento.escolhido && (
                                <div style={{
                                  backgroundColor: '#10B981',
                                  color: 'white',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  padding: '0.25rem 0.5rem',
                                  display: 'inline-block',
                                  borderRadius: '4px'
                                }}>
                                  Escolhido
                                </div>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-3">
                              <div>
                                <p className={`${orcamento.escolhido ? 'text-green-700' : 'text-gray-700'}`}>
                                  <span className="font-medium">Condição:</span> {orcamento.condicao_peca}
                                </p>
                                <p className={`${orcamento.escolhido ? 'text-green-700' : 'text-gray-700'}`}>
                                  <span className="font-medium">Garantia:</span> {orcamento.garantia}
                                </p>
                                <p className={`${orcamento.escolhido ? 'text-green-700' : 'text-gray-700'}`}>
                                  <span className="font-medium">Tempo de Envio:</span> {orcamento.tempo_envio}
                                </p>
                              </div>
                              <div>
                                <p className={`${orcamento.escolhido ? 'text-green-700' : 'text-gray-700'}`}>
                                  <span className="font-medium">Tipo de Entrega:</span> {orcamento.tipo_entrega}
                                </p>
                                <p className={`${orcamento.escolhido ? 'text-green-700' : 'text-gray-700'}`}>
                                  <span className="font-medium">Frete Incluso:</span> {orcamento.frete_incluso ? 'Sim' : 'Não'}
                                </p>
                              </div>
                            </div>
                            
                            {orcamento.observacoes && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className={`text-sm ${orcamento.escolhido ? 'text-green-600' : 'text-gray-600'}`}>
                                  <span className="font-medium">Observações:</span> {orcamento.observacoes}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Imagem */}
                {pedido.imagem && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Imagem</h3>
                    <div className="border rounded-lg p-4">
                      <img
                        src={pedido.imagem}
                        alt="Imagem do pedido"
                        className="w-full h-64 object-cover rounded"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Actions */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações</h3>
              <div className="space-y-3">
                {pedido.status === 'em_andamento' && (
                  <button
                    onClick={() => navigate(`/cliente/orcamentos/${pedido.id}`)}
                    className="btn btn-primary w-full"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Ver Orçamentos
                  </button>
                )}
                
                <button
                  onClick={handleDuplicarPedido}
                  className="btn btn-outline w-full"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicar Pedido
                </button>
                
                <button
                  onClick={() => navigate('/cliente/dashboard')}
                  className="btn btn-outline w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar ao Dashboard
                </button>
              </div>
            </div>

            {/* Status Timeline */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Pedido Criado</p>
                    <p className="text-xs text-gray-500">
                      {pedido.status_timeline?.criado ? 
                        new Date(pedido.status_timeline.criado).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 
                        new Date(pedido.data_criacao).toLocaleDateString('pt-BR')
                      }
                    </p>
                  </div>
                </div>
                
                {pedido.status_timeline?.em_andamento && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Em Andamento</p>
                      <p className="text-xs text-gray-500">
                        {new Date(pedido.status_timeline.em_andamento).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}
                
                {(pedido.status_timeline?.orcamentos_recebidos || (pedido.orcamentos_timeline && pedido.orcamentos_timeline.length > 0)) && (
                  <div className="space-y-2">
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Orçamentos Recebidos</p>
                        <p className="text-xs text-gray-500">
                          {pedido.status_timeline?.orcamentos_recebidos ? 
                            new Date(pedido.status_timeline.orcamentos_recebidos).toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 
                            new Date().toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          } - {pedido.orcamentos_count} propostas
                        </p>
                    </div>
                    </div>
                    
                    {/* Orçamentos individuais */}
                    {pedido.orcamentos_timeline && pedido.orcamentos_timeline.length > 0 && (
                      <div className="ml-6 space-y-2">
                        {pedido.orcamentos_timeline.map((orcamento, index) => (
                          <div key={orcamento.id} className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            <div className="flex-1">
                              <p className="text-xs font-medium text-gray-700">{orcamento.fornecedor}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(orcamento.data_recebimento).toLocaleString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })} - R$ {orcamento.valor.toLocaleString('pt-BR')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {pedido.status_timeline?.aprovado && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Aprovado</p>
                      <p className="text-xs text-gray-500">
                        {new Date(pedido.status_timeline.aprovado).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {pedido.status_timeline?.finalizado && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Finalizado</p>
                      <p className="text-xs text-gray-500">
                        {new Date(pedido.status_timeline.finalizado).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}

                {pedido.status_timeline?.reprovado && (
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Reprovado</p>
                      <p className="text-xs text-gray-500">
                        {new Date(pedido.status_timeline.reprovado).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Cancelamento */}
      {showCancelModal && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100vw', 
            height: '100vh',
            margin: 0,
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-8 h-8 text-orange-500 mr-3" />
              <h3 className="text-xl font-semibold text-gray-900">⚠️ Confirmar Cancelamento</h3>
            </div>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <p className="text-orange-800 font-medium mb-2">
                ⚠️ ATENÇÃO: Esta ação não pode ser desfeita!
              </p>
              <p className="text-orange-700 text-sm">
                Cancelar este pedido irá:
              </p>
              <ul className="text-orange-700 text-sm mt-2 ml-4 space-y-1">
                <li>• Notificar todos os fornecedores participantes</li>
                <li>• Encerrar permanentemente este pedido</li>
                <li>• Impossibilitar receber novos orçamentos</li>
              </ul>
            </div>
            
            <p className="text-gray-600 mb-4 font-medium">
              Tem certeza absoluta que deseja cancelar o pedido #{pedido?.id}?
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo do cancelamento (opcional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                rows={3}
                placeholder="Digite o motivo do cancelamento..."
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                className="btn btn-outline"
                disabled={isActionLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleCancelPedido}
                className="btn bg-orange-600 hover:bg-orange-700 text-white"
                disabled={isActionLoading}
              >
                {isActionLoading ? 'Cancelando...' : '⚠️ SIM, Cancelar Definitivamente'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Edição (placeholder) */}
      {showEditModal && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100vw', 
            height: '100vh',
            margin: 0,
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Editar Pedido</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <p className="text-gray-600 mb-4">
              Funcionalidade de edição será implementada em breve.
            </p>
            
            <div className="flex justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-outline"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de Adicionar Informações */}
      {showAddInfoModal && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100vw', 
            height: '100vh',
            margin: 0,
            padding: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                <Plus className="w-5 h-5 inline mr-2" />
                Adicionar Informações Complementares
              </h3>
              <button
                onClick={() => {
                  setShowAddInfoModal(false);
                  setAdditionalInfo('');
                  setSelectedFiles(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <p className="text-gray-600 mb-6">
              Adicione informações complementares que podem ajudar os fornecedores a enviar orçamentos mais precisos.
            </p>
            
            {/* Campo de observações */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações Adicionais
              </label>
              <textarea
                value={additionalInfo}
                onChange={(e) => setAdditionalInfo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder="Digite informações adicionais sobre o pedido, especificações técnicas, localização, urgência, etc..."
              />
              <p className="text-sm text-gray-500 mt-1">
                Estas informações serão adicionadas ao histórico do pedido e todos os fornecedores participantes serão notificados.
              </p>
            </div>
            
            {/* Upload de arquivos */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Arquivos Complementares (Opcional)
              </label>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                onChange={(e) => setSelectedFiles(e.target.files)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-sm text-gray-500 mt-1">
                Máximo 5 arquivos. Formatos aceitos: PDF, DOC, DOCX, JPG, PNG, TXT
              </p>
              
              {selectedFiles && selectedFiles.length > 0 && (
                <div className="mt-2">
                  <p className="text-sm font-medium text-gray-700">Arquivos selecionados:</p>
                  <ul className="text-sm text-gray-600 mt-1">
                    {Array.from(selectedFiles).map((file, index) => (
                      <li key={index} className="flex items-center">
                        <FileText className="w-4 h-4 mr-1" />
                        {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddInfoModal(false);
                  setAdditionalInfo('');
                  setSelectedFiles(null);
                }}
                className="btn btn-outline"
                disabled={isActionLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleAddInfo}
                className="btn btn-primary"
                disabled={isActionLoading || (!additionalInfo.trim() && !selectedFiles)}
              >
                {isActionLoading ? 'Adicionando...' : 'Adicionar Informações'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      
    </div>
  );
};

export default PedidoDetalhes; 